/**
 * "What's New" announcement framework — model.
 *
 * An announcement is one entry in the small release-notes modal (Cursor-style)
 * that greets returning users when something shipped since their last visit.
 * To announce a feature, append a WhatsNewEntry to WHATS_NEW_ENTRIES in
 * shared/config/whats-new-entries.ts — no component changes needed. Per-user
 * "seen" tracking keys off the entry id, so ids must be stable and never reused.
 */

/**
 * Curated banner themes for the panel header — self-contained CSS artwork in
 * whats-new-modal.component.scss ([data-theme='…'] blocks). The admin picks
 * one per release in the Admin portal (settings.header_theme); unknown/empty
 * values fall back to 'ember'. Keep in sync with the Admin portal's
 * announcement-themes.ts and the backend's KNOWN_HEADER_THEMES.
 */
export const WHATS_NEW_HEADER_THEMES =
  ['ember', 'aurora', 'ocean', 'forest', 'midnight', 'dawn'] as const;
export type WhatsNewHeaderTheme = (typeof WHATS_NEW_HEADER_THEMES)[number];

export const DEFAULT_WHATS_NEW_THEME: WhatsNewHeaderTheme = 'ember';

export function coerceHeaderTheme(value: string | undefined | null): WhatsNewHeaderTheme {
  return (WHATS_NEW_HEADER_THEMES as readonly string[]).includes(value ?? '')
    ? (value as WhatsNewHeaderTheme)
    : DEFAULT_WHATS_NEW_THEME;
}

export interface WhatsNewCta {
  label: string;
  /** Router link to navigate to on click (e.g. '/workspace'). */
  route?: string;
  /**
   * Named in-app action, for destinations that aren't routes (modals, tours).
   * Known ids ('open-abilities', 'open-catalog') are handled in
   * WhatsNewModalComponent.runCta — extend the switch there when introducing a
   * new action. Plain string because entries now arrive from the admin API.
   */
  actionId?: string;
}

export interface WhatsNewEntry {
  /** Stable unique id — the per-user seen flag keys off this. Never reuse one. */
  id: string;
  /** PrimeIcons class for the entry glyph, e.g. 'pi pi-compass'. */
  icon: string;
  title: string;
  description: string;
  /** ISO date (YYYY-MM-DD) the feature shipped; entries render newest first. */
  date: string;
  /** Whole entry row becomes clickable when set (subtle ↗ affordance). */
  cta?: WhatsNewCta;
  /**
   * Nav-badge anchor: while this entry is unseen, the nav item that calls
   * WhatsNewService.hasUnseenBadge('<anchor>') shows a small "New" pill; it
   * disappears once the announcement is seen. This is THE way to badge a nav
   * entry for a launch — never hardcode a pill in a template again.
   */
  navBadge?: string;
  /**
   * Spotlight this entry at the top of the panel — larger type, optional
   * artwork, and the CTA promoted to a real button. Only the newest flagged
   * entry is spotlighted (see WhatsNewService.featured); the rest fall into the
   * compact digest, so the panel can never become a stack of heroes.
   */
  featured?: boolean;
  /** https:// artwork for a spotlighted entry. Optional — without it the
   *  spotlight keeps its emphasis and simply drops the image strip. */
  imageUrl?: string;
}
